import logoUrl from '@/assets/sectfit-logo.png';
import { cn } from '@/lib/cn';

// -----------------------------------------------------------------------------
// The SECT.FIT mark.
//
// The same icon the mobile app uses - `Frontend/src/Shared/assets/images/
// index.ts` serves it from a FlutterFlow storage URL. It is BUNDLED here rather
// than hot-linked, for three reasons:
//
//   1. This panel is meant to run behind a strict CSP with no external origins.
//      A remote <img> is the one thing that would break that.
//   2. An admin tool that shows a broken image when a third-party bucket has a
//      bad morning is not a good look for the surface staff trust least.
//   3. Vite fingerprints and long-caches it, so it costs one request ever.
//
// If the app's mark changes, re-run:
//   curl -L -o src/assets/sectfit-logo.png "<APP_LOGO_URL>"
// -----------------------------------------------------------------------------

const SIZES = {
  sm: { tile: 'size-8', radius: 'rounded-[7px]', word: 'text-base', tag: 'text-[10px]' },
  md: { tile: 'size-10', radius: 'rounded-[9px]', word: 'text-lg', tag: 'text-xs' },
  lg: { tile: 'size-14', radius: 'rounded-[12px]', word: 'text-2xl', tag: 'text-sm' },
  xl: { tile: 'size-[72px]', radius: 'rounded-[16px]', word: 'text-3xl', tag: 'text-base' },
} as const;

export type BrandMarkSize = keyof typeof SIZES;

/**
 * Logo tile alone.
 *
 * `alt=""` and `aria-hidden` wherever a wordmark sits beside it - otherwise a
 * screen reader announces "SECT.FIT logo, SECT.FIT", which is noise. The
 * standalone case passes a real alt.
 */
export function BrandLogo({
  size = 'md',
  className,
  alt = '',
}: {
  size?: BrandMarkSize;
  className?: string;
  alt?: string;
}): JSX.Element {
  const scale = SIZES[size];

  return (
    <img
      src={logoUrl}
      alt={alt}
      aria-hidden={alt === '' ? true : undefined}
      // `object-cover` on a square source is a no-op, but it guarantees the tile
      // never letterboxes if the mark is ever replaced with a non-square file.
      className={cn(
        scale.tile,
        scale.radius,
        'shrink-0 object-cover shadow-card ring-1 ring-black/5',
        className,
      )}
      draggable={false}
    />
  );
}

/**
 * Logo plus wordmark.
 *
 * `SECT` is 700 and `.FIT` is 400 in the brand violet - the same weight break
 * the mobile app's `BrandMark` uses, so the three surfaces read as one product.
 */
export function BrandMark({
  size = 'md',
  tagline,
  inverted = false,
  className,
}: {
  size?: BrandMarkSize;
  /** Small line under the wordmark - "Admin", "Console". */
  tagline?: string;
  /** For the deep-violet login panel, where the wordmark must be white. */
  inverted?: boolean;
  className?: string;
}): JSX.Element {
  const scale = SIZES[size];

  return (
    <div className={cn('flex min-w-0 items-center gap-2.5', className)}>
      <BrandLogo size={size} className={inverted ? 'ring-white/15' : undefined} />

      <div className="min-w-0">
        <p
          className={cn(
            scale.word,
            'truncate font-bold leading-none tracking-tight',
            inverted ? 'text-white' : 'text-foreground',
          )}
        >
          SECT
          <span className={cn('font-normal', inverted ? 'text-lavender' : 'text-primary')}>
            .FIT
          </span>
        </p>

        {tagline ? (
          <p
            className={cn(
              scale.tag,
              'mt-1 truncate font-medium uppercase leading-none tracking-[0.14em]',
              // On the violet panel `text-lavender` is #C8A4F6 on #450098, which
              // clears AA comfortably. On white it would not, so the light case
              // uses the muted neutral instead.
              inverted ? 'text-lavender' : 'text-muted-foreground',
            )}
          >
            {tagline}
          </p>
        ) : null}
      </div>
    </div>
  );
}
